/**
 * StoreLocator — Local Game Store (LGS) Finder
 * 
 * Data sources (in priority order):
 *   1. Wizards of the Coast GraphQL API — official WPN store data
 *      Endpoint: https://api.tabletop.wizards.com/silverbeak-griffin-service/graphql
 *      Query: getStoresByLocation(latitude, longitude, maxMeters, pageSize, page)
 *      CORS: Access-Control-Allow-Origin: * (browser-safe)
 *   2. OpenStreetMap Overpass API — community-sourced game shops (fallback)
 *   3. External links to Google Maps & Wizards locator page
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { GameStore, GeoPosition } from './types';
import {
    MapPin, Navigation, Phone, Globe, ExternalLink,
    Calendar, Search, Loader2, AlertTriangle,
    ChevronDown, ChevronUp, Locate, Store, ShieldCheck, Info, Trash2, Star
} from 'lucide-react';

interface StoreLocatorProps {
    currency: 'usd' | 'eur';
}

// ===== Wizards GraphQL API =====
const WIZARDS_GRAPHQL_URL = 'https://api.tabletop.wizards.com/silverbeak-griffin-service/graphql';
// Supabase Edge Function proxy — bypasses CORS
const WPN_PROXY_URL = 'https://yytoqcneczoxwegwtuep.supabase.co/functions/v1/wpn-store-search';

const STORES_BY_LOCATION_QUERY = `
query getStoresByLocation(
  $latitude: Float!, $longitude: Float!, $maxMeters: Int!,
  $pageSize: Int!, $page: Int!
) {
  storesByLocation(input: {
    latitude: $latitude, longitude: $longitude, maxMeters: $maxMeters,
    pageSize: $pageSize, page: $page
  }) {
    stores {
      id isPremium latitude longitude name postalAddress
      distance phoneNumber website
    }
    pageInfo { page pageSize totalResults }
  }
}`;

// Calculate distance between two lat/lng coordinates (Haversine formula)
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 3959; // miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Convert meters to miles
function metersToMiles(meters: number): number {
    return meters / 1609.34;
}

// Detect if a query looks like a US zip code (5 digits or 5+4)
function isUsZipCode(q: string): boolean {
    return /^\d{5}(-\d{4})?$/.test(q.trim());
}

// Parse the WPN postalAddress string into components
function parseWpnAddress(address: string): { street: string; city: string; state: string; zip: string; country: string } {
    // Format: "Street\nCity, State, Zip\nCountry" or "Street\nCity, State, Zip, Country"
    const lines = address.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length >= 3) {
        const street = lines[0];
        const cityLine = lines[1]; // "City, State, Zip"
        const country = lines[2];
        const parts = cityLine.split(',').map(s => s.trim());
        return {
            street,
            city: parts[0] || '',
            state: parts[1] || '',
            zip: parts[2] || '',
            country,
        };
    }
    if (lines.length === 2) {
        const street = lines[0];
        const rest = lines[1].split(',').map(s => s.trim());
        return {
            street,
            city: rest[0] || '',
            state: rest[1] || '',
            zip: rest[2] || '',
            country: rest[3] || '',
        };
    }
    return { street: address, city: '', state: '', zip: '', country: '' };
}

type DataSourceType = 'wpn' | 'osm' | 'both' | 'none';

export function StoreLocator({ currency }: StoreLocatorProps) {
    const [userLocation, setUserLocation] = useState<GeoPosition | null>(null);
    const [resolvedLocationName, setResolvedLocationName] = useState<string | null>(null);
    const [isLocating, setIsLocating] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [stores, setStores] = useState<GameStore[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [maxDistance, setMaxDistance] = useState(50); // miles
    const [showWpnOnly, setShowWpnOnly] = useState(false);
    const [expandedStore, setExpandedStore] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [dataSource, setDataSource] = useState<DataSourceType>('none');
    const [totalWpnResults, setTotalWpnResults] = useState(0);
    const [wpnApiFailed, setWpnApiFailed] = useState(false);

    // Request user location via browser geolocation
    const requestLocation = useCallback(() => {
        setIsLocating(true);
        setLocationError(null);

        if (!navigator.geolocation) {
            setLocationError('Geolocation is not supported by your browser');
            setIsLocating(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                });
                setResolvedLocationName('Your Location');
                setIsLocating(false);
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setLocationError('Location permission denied. You can search by city/zip instead.');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setLocationError('Location unavailable. Try searching by city or zip code.');
                        break;
                    case error.TIMEOUT:
                        setLocationError('Location request timed out. Try again or search by city.');
                        break;
                }
                setIsLocating(false);
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
    }, []);

    // Geocode a text query to lat/lng using Nominatim
    const geocodeQuery = async (query: string): Promise<{ lat: number; lng: number; displayName: string } | null> => {
        const trimmed = query.trim();
        if (!trimmed) return null;

        try {
            const isZip = isUsZipCode(trimmed);
            let url = `https://nominatim.openstreetmap.org/search?format=json&limit=3&addressdetails=1`;

            if (isZip) {
                url += `&postalcode=${encodeURIComponent(trimmed)}&countrycodes=us`;
            } else {
                url += `&q=${encodeURIComponent(trimmed)}&countrycodes=us`;
            }

            const res = await fetch(url, { headers: { 'Accept-Language': 'en-US,en' } });

            if (res.ok) {
                const data = await res.json();
                if (data.length > 0) {
                    const best = data[0];
                    const city = best.address?.city || best.address?.town || best.address?.village || best.address?.county || '';
                    const state = best.address?.state || '';
                    const displayName = [city, state].filter(Boolean).join(', ') || best.display_name?.split(',').slice(0, 2).join(',') || trimmed;

                    return { lat: parseFloat(best.lat), lng: parseFloat(best.lon), displayName };
                }
            }

            // If US-biased search returned nothing for text queries, try without country bias
            if (!isZip) {
                const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(trimmed)}`;
                const fallbackRes = await fetch(fallbackUrl, { headers: { 'Accept-Language': 'en-US,en' } });
                if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    if (fallbackData.length > 0) {
                        return {
                            lat: parseFloat(fallbackData[0].lat),
                            lng: parseFloat(fallbackData[0].lon),
                            displayName: fallbackData[0].display_name?.split(',').slice(0, 2).join(',') || trimmed,
                        };
                    }
                }
            }
        } catch {
            // Geocoding failed
        }

        return null;
    };

    // ===== Fetch stores from Wizards GraphQL API (via Edge Function proxy) =====
    const fetchWpnStores = async (
        lat: number, lng: number, radiusMiles: number
    ): Promise<{ stores: GameStore[]; total: number; failed: boolean }> => {
        const maxMeters = Math.round(radiusMiles * 1609.34);

        const parseWpnResponse = (data: any, srcLat: number, srcLng: number): { stores: GameStore[]; total: number } => {
            const storeData = data?.data?.storesByLocation;
            if (!storeData?.stores) return { stores: [], total: 0 };

            const wpnStores: GameStore[] = storeData.stores.map((s: any) => {
                const parsed = parseWpnAddress(s.postalAddress || '');
                const distanceMiles = s.distance ? metersToMiles(s.distance) : haversineDistance(srcLat, srcLng, s.latitude, s.longitude);

                return {
                    id: `wpn-${s.id}`,
                    name: s.name || 'Unknown Store',
                    address: parsed.street,
                    city: parsed.city,
                    state: parsed.state,
                    country: parsed.country,
                    zipCode: parsed.zip,
                    phone: s.phoneNumber || undefined,
                    website: s.website || undefined,
                    email: undefined,
                    distance: distanceMiles,
                    lat: s.latitude || 0,
                    lng: s.longitude || 0,
                    isWpnStore: true,
                    isPremium: s.isPremium || false,
                    storeType: 'lgs' as const,
                    hasEvents: true,
                    rating: undefined,
                };
            });

            return { stores: wpnStores, total: storeData.pageInfo?.totalResults || wpnStores.length };
        };

        // 1) Try Supabase Edge Function proxy (bypasses CORS)
        try {
            const proxyRes = await fetch(WPN_PROXY_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl5dG9xY25lY3pveHdlZ3d0dWVwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgwODM1MDgsImV4cCI6MjA4MzY1OTUwOH0.AapY3VenT2om6UHhxa5KmM4UxeilQ_DRsct2ubzi1Gs',
                },
                body: JSON.stringify({ latitude: lat, longitude: lng, maxMeters, pageSize: 50, page: 1 }),
            });

            if (proxyRes.ok) {
                const json = await proxyRes.json();
                const result = parseWpnResponse(json, lat, lng);
                return { ...result, failed: false };
            }
            console.warn('WPN proxy returned', proxyRes.status, '— falling back to direct API');
        } catch (proxyErr) {
            console.warn('WPN proxy unavailable:', proxyErr);
        }

        // 2) Fallback: try direct Wizards API (may be blocked by CORS)
        try {
            const res = await fetch(WIZARDS_GRAPHQL_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: STORES_BY_LOCATION_QUERY,
                    variables: { latitude: lat, longitude: lng, maxMeters, pageSize: 50, page: 1 },
                }),
            });

            if (!res.ok) return { stores: [], total: 0, failed: true };

            const json = await res.json();
            const result = parseWpnResponse(json, lat, lng);
            return { ...result, failed: false };
        } catch (e) {
            console.warn('WPN API unavailable (likely CORS):', e);
            return { stores: [], total: 0, failed: true };
        }
    };

    // ===== Fetch stores from OpenStreetMap Overpass =====
    // Broad query covering all shop types that might sell TCGs/games
    const fetchOsmStores = async (lat: number, lng: number, radiusMiles: number): Promise<GameStore[]> => {
        const radiusMeters = radiusMiles * 1609;

        try {
            // Broad query: game shops, comic shops, hobby shops, collectors, toy stores,
            // card shops, bookstores (many carry games), and any shop with game/card/comic in name
            const overpassQuery = `
[out:json][timeout:30];
(
  node["shop"="games"](around:${radiusMeters},${lat},${lng});
  way["shop"="games"](around:${radiusMeters},${lat},${lng});
  node["shop"="collector"](around:${radiusMeters},${lat},${lng});
  way["shop"="collector"](around:${radiusMeters},${lat},${lng});
  node["shop"="anime"](around:${radiusMeters},${lat},${lng});
  way["shop"="anime"](around:${radiusMeters},${lat},${lng});
  node["shop"="comic"](around:${radiusMeters},${lat},${lng});
  way["shop"="comic"](around:${radiusMeters},${lat},${lng});
  node["shop"="toys"](around:${radiusMeters},${lat},${lng});
  way["shop"="toys"](around:${radiusMeters},${lat},${lng});
  node["shop"="hobby"](around:${radiusMeters},${lat},${lng});
  way["shop"="hobby"](around:${radiusMeters},${lat},${lng});
  node["shop"="trade"](around:${radiusMeters},${lat},${lng});
  way["shop"="trade"](around:${radiusMeters},${lat},${lng});
  node["shop"="books"]["name"~"[Gg]ame|[Cc]omic|[Cc]ard|[Hh]obby|[Cc]ollect"](around:${radiusMeters},${lat},${lng});
  way["shop"="books"]["name"~"[Gg]ame|[Cc]omic|[Cc]ard|[Hh]obby|[Cc]ollect"](around:${radiusMeters},${lat},${lng});
  node["shop"]["name"~"[Gg]ame [Ss]hop|[Cc]ard [Ss]hop|[Cc]omic|TCG|MTG|Pokemon|[Tt]rading [Cc]ard|[Cc]ollectible"](around:${radiusMeters},${lat},${lng});
  way["shop"]["name"~"[Gg]ame [Ss]hop|[Cc]ard [Ss]hop|[Cc]omic|TCG|MTG|Pokemon|[Tt]rading [Cc]ard|[Cc]ollectible"](around:${radiusMeters},${lat},${lng});
  node["leisure"="amusement_arcade"]["name"~"[Gg]ame|[Cc]ard"](around:${radiusMeters},${lat},${lng});
  way["leisure"="amusement_arcade"]["name"~"[Gg]ame|[Cc]ard"](around:${radiusMeters},${lat},${lng});
);
out body center;
`.trim().replace(/\n\s*/g, '');

            const overpassRes = await fetch(
                `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(overpassQuery)}`
            );

            if (!overpassRes.ok) return [];

            const overpassData = await overpassRes.json();
            if (!overpassData.elements) return [];

            // Deduplicate elements (same node can appear from multiple query branches)
            const seen = new Set<number>();
            return overpassData.elements
                .filter((el: any) => {
                    if (!el.tags?.name || seen.has(el.id)) return false;
                    seen.add(el.id);
                    return true;
                })
                .map((el: any) => {
                    const elLat = el.lat ?? el.center?.lat ?? 0;
                    const elLon = el.lon ?? el.center?.lon ?? 0;
                    const tags = el.tags || {};

                    return {
                        id: `osm-${el.id}`,
                        name: tags.name,
                        address: [tags['addr:housenumber'], tags['addr:street']].filter(Boolean).join(' ') || '',
                        city: tags['addr:city'] || '',
                        state: tags['addr:state'] || '',
                        country: tags['addr:country'] || '',
                        zipCode: tags['addr:postcode'] || '',
                        phone: tags.phone || tags['contact:phone'] || undefined,
                        website: tags.website || tags['contact:website'] || undefined,
                        email: tags.email || tags['contact:email'] || undefined,
                        distance: haversineDistance(lat, lng, elLat, elLon),
                        lat: elLat,
                        lng: elLon,
                        isWpnStore: false,
                        isPremium: false,
                        storeType: 'lgs' as const,
                        hasEvents: !!tags.opening_hours,
                        rating: undefined,
                        openingHours: tags.opening_hours || undefined,
                    };
                });
        } catch {
            return [];
        }
    };

    // ===== Main search function =====
    const searchStores = useCallback(async (query?: string) => {
        setIsLoading(true);
        setHasSearched(true);
        setLocationError(null);

        try {
            let searchLat = userLocation?.lat;
            let searchLng = userLocation?.lng;

            // If searching by text, geocode first
            if (query && query.trim()) {
                const geo = await geocodeQuery(query);
                if (geo) {
                    searchLat = geo.lat;
                    searchLng = geo.lng;
                    setUserLocation({ lat: searchLat, lng: searchLng });
                    setResolvedLocationName(geo.displayName);
                } else {
                    setLocationError(`Could not find location "${query}". Try a different search term.`);
                    setIsLoading(false);
                    return;
                }
            }

            if (searchLat == null || searchLng == null) {
                setLocationError('Could not determine search location. Enable location or search by city/zip.');
                setIsLoading(false);
                return;
            }

            // Fetch from both sources in parallel
            const [wpnResult, osmStores] = await Promise.all([
                fetchWpnStores(searchLat, searchLng, maxDistance),
                fetchOsmStores(searchLat, searchLng, maxDistance),
            ]);

            setTotalWpnResults(wpnResult.total);
            setWpnApiFailed(wpnResult.failed);

            // Merge and deduplicate — WPN stores take priority
            // If an OSM store is within ~200m of a WPN store with similar name, skip it
            const wpnStores = wpnResult.stores;
            const deduped = osmStores.filter(osmStore => {
                return !wpnStores.some(wpnStore => {
                    const dist = haversineDistance(osmStore.lat, osmStore.lng, wpnStore.lat, wpnStore.lng);
                    if (dist < 0.15) return true; // within ~250m, likely same store
                    // Check name similarity
                    const nameSim = osmStore.name.toLowerCase().includes(wpnStore.name.toLowerCase().split(' ')[0]) ||
                        wpnStore.name.toLowerCase().includes(osmStore.name.toLowerCase().split(' ')[0]);
                    return nameSim && dist < 1;
                });
            });

            const allStores = [...wpnStores, ...deduped];
            allStores.sort((a, b) => (a.distance ?? 999) - (b.distance ?? 999));

            setStores(allStores);

            // Determine data source for UI
            if (wpnStores.length > 0 && deduped.length > 0) {
                setDataSource('both');
            } else if (wpnStores.length > 0) {
                setDataSource('wpn');
            } else if (deduped.length > 0) {
                setDataSource('osm');
            } else {
                setDataSource('none');
            }

            // Auto-disable WPN filter if WPN API failed (no WPN data to filter by)
            if (wpnResult.failed) {
                setShowWpnOnly(false);
            }
        } catch (e) {
            console.error('Store search failed:', e);
        }

        setIsLoading(false);
    }, [userLocation, maxDistance]);

    // Auto-search when location becomes available
    useEffect(() => {
        if (userLocation && !hasSearched) {
            searchStores();
        }
    }, [userLocation, hasSearched]);

    // Build Wizards Locator URL with pre-filled search
    const wizardsLocatorUrl = useMemo(() => {
        const base = 'https://locator.wizards.com/';
        if (searchQuery) {
            return `${base}search?searchType=stores&query=${encodeURIComponent(searchQuery)}&distance=100&page=1&sort=date&sortDirection=Asc`;
        }
        return base;
    }, [searchQuery]);

    // Google Maps search URL
    const googleMapsUrl = useMemo(() => {
        const q = searchQuery
            ? `game store trading cards near ${searchQuery}`
            : userLocation
                ? `game store trading cards near ${userLocation.lat},${userLocation.lng}`
                : 'game store trading cards near me';
        return `https://www.google.com/maps/search/${encodeURIComponent(q)}`;
    }, [searchQuery, userLocation]);

    // Filtered + sorted stores
    const displayStores = useMemo(() => {
        let filtered = stores;
        if (showWpnOnly) {
            filtered = filtered.filter(s => s.isWpnStore);
        }
        filtered.sort((a, b) => (a.distance ?? 999) - (b.distance ?? 999));
        return filtered;
    }, [stores, showWpnOnly]);

    const wpnCount = useMemo(() => stores.filter(s => s.isWpnStore).length, [stores]);
    const osmCount = useMemo(() => stores.filter(s => !s.isWpnStore).length, [stores]);

    const handleClear = () => {
        setStores([]);
        setHasSearched(false);
        setDataSource('none');
        setResolvedLocationName(null);
        setUserLocation(null);
        setSearchQuery('');
        setTotalWpnResults(0);
        setExpandedStore(null);
    };

    return (
        <div className="space-y-4">
            {/* ========== HEADER ========== */}
            <div className="bg-gradient-to-r from-purple-900/30 via-indigo-900/20 to-purple-900/30 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                        <MapPin className="size-5 text-emerald-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white">Find Your LGS</h3>
                        <p className="text-xs text-white/40">
                            Discover WPN stores and local game shops near you
                        </p>
                    </div>
                </div>

                {/* Location + search bar */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={requestLocation}
                        disabled={isLocating}
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium transition-all border border-emerald-500/30 disabled:opacity-50"
                    >
                        {isLocating ? (
                            <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                            <Locate className="size-3.5" />
                        )}
                        {userLocation ? 'Update Location' : 'Use My Location'}
                    </button>

                    <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') searchStores(searchQuery); }}
                            placeholder="City, state, or zip code (e.g. Corning NY, 14830)..."
                            className="w-full pl-8 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50"
                        />
                    </div>

                    <select
                        value={maxDistance}
                        onChange={e => setMaxDistance(parseInt(e.target.value))}
                        className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-xs text-white focus:outline-none"
                    >
                        <option value={10}>10 mi</option>
                        <option value={25}>25 mi</option>
                        <option value={50}>50 mi</option>
                        <option value={100}>100 mi</option>
                    </select>

                    <button
                        onClick={() => searchStores(searchQuery || undefined)}
                        disabled={isLoading}
                        className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg text-xs font-medium transition-all border border-amber-500/30 disabled:opacity-50"
                    >
                        {isLoading ? <Loader2 className="size-3.5 animate-spin" /> : 'Search'}
                    </button>
                </div>

                {/* Location status */}
                {userLocation && resolvedLocationName && (
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-400/60">
                        <Navigation className="size-3" />
                        Searching near {resolvedLocationName} · {maxDistance} mile radius
                    </div>
                )}
                {locationError && (
                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-amber-400/60">
                        <AlertTriangle className="size-3" />
                        {locationError}
                    </div>
                )}
            </div>

            {/* ========== EXTERNAL LINKS ========== */}
            <div className="grid grid-cols-2 gap-3">
                <a
                    href={wizardsLocatorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-xl text-xs font-medium transition-all border border-blue-500/20 hover:border-blue-500/40"
                >
                    <ShieldCheck className="size-3.5" />
                    WPN Locator on Wizards.com
                    <ExternalLink className="size-3 opacity-60" />
                </a>
                <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-xl text-xs font-medium transition-all border border-emerald-500/20 hover:border-emerald-500/40"
                >
                    <MapPin className="size-3.5" />
                    Search on Google Maps
                    <ExternalLink className="size-3 opacity-60" />
                </a>
            </div>

            {/* ========== FILTERS & COUNTS ========== */}
            {stores.length > 0 && !wpnApiFailed && (
                <div className="flex items-center gap-3 flex-wrap">
                    <button
                        onClick={() => setShowWpnOnly(p => !p)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${showWpnOnly
                            ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                            : 'bg-white/5 text-white/40 border-white/10 hover:text-white/60'
                            }`}
                    >
                        <ShieldCheck className="size-3" />
                        WPN Stores Only
                        {wpnCount > 0 && (
                            <span className="text-[9px] bg-blue-500/30 text-blue-300 px-1 rounded-full ml-0.5">{wpnCount}</span>
                        )}
                    </button>

                    {osmCount > 0 && (
                        <span className="text-[10px] text-white/25 flex items-center gap-1">
                            <Info className="size-3" />
                            +{osmCount} from OpenStreetMap
                        </span>
                    )}

                    <div className="flex-1" />

                    <button
                        onClick={handleClear}
                        className="flex items-center gap-1 px-2 py-1 bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/20 rounded-lg text-[10px] text-white/40 hover:text-red-400 transition-colors"
                    >
                        <Trash2 className="size-3" />
                        Clear
                    </button>

                    <span className="text-[10px] text-white/30">
                        {displayStores.length} store{displayStores.length !== 1 ? 's' : ''} found
                    </span>
                </div>
            )}

            {/* ========== LOADING ========== */}
            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="size-8 text-amber-400 animate-spin" />
                        <span className="text-sm text-white/40">Searching WPN directory & OpenStreetMap...</span>
                    </div>
                </div>
            )}

            {/* ========== EMPTY / FALLBACK STATE ========== */}
            {!isLoading && displayStores.length === 0 && hasSearched && (
                <div className="space-y-3">
                    {/* WPN API blocked — show prominent Wizards Locator card */}
                    {wpnApiFailed && (
                        <a
                            href={wizardsLocatorUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-xl border border-blue-500/25 bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-purple-500/10 p-4 hover:border-blue-500/40 transition-all group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                                    <ShieldCheck className="size-5 text-blue-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-semibold text-white group-hover:text-blue-300 transition-colors flex items-center gap-2">
                                        Browse WPN Stores on Wizards.com
                                        <ExternalLink className="size-3 text-blue-400/60" />
                                    </div>
                                    <p className="text-[11px] text-white/40 mt-0.5">
                                        Find official WPN stores, events, and tournaments near {resolvedLocationName || 'you'}
                                    </p>
                                </div>
                                <div className="text-[9px] px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30 flex-shrink-0">
                                    OFFICIAL WPN
                                </div>
                            </div>
                        </a>
                    )}

                    {/* Google Maps embed — show as primary result */}
                    {userLocation && (
                        <div className="rounded-xl overflow-hidden border border-emerald-500/20">
                            <div className="bg-emerald-500/[0.06] px-3 py-2 flex items-center gap-2 border-b border-emerald-500/15">
                                <MapPin className="size-3.5 text-emerald-400" />
                                <span className="text-xs text-white/60">Google Maps — nearby card & game shops</span>
                                <a
                                    href={googleMapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300"
                                >
                                    Open in Maps <ExternalLink className="size-3" />
                                </a>
                            </div>
                            <iframe
                                title="Google Maps store search"
                                width="100%"
                                height="350"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                src={`https://maps.google.com/maps?q=${encodeURIComponent(
                                    `card shop game store near ${resolvedLocationName || `${userLocation.lat},${userLocation.lng}`}`
                                )}&t=&z=12&ie=UTF8&iwloc=&output=embed`}
                            />
                        </div>
                    )}

                    {/* Subtle info note */}
                    <div className="flex items-center justify-center gap-2 text-[10px] text-white/25 px-4">
                        <Info className="size-3 flex-shrink-0" />
                        {showWpnOnly
                            ? 'No WPN stores found. Try disabling the "WPN Stores Only" filter or expanding your search radius.'
                            : wpnApiFailed
                                ? 'WPN directory requires Wizards.com — OpenStreetMap had no additional listings here.'
                                : 'No listings found in WPN or OpenStreetMap databases for this area.'
                        }
                    </div>
                </div>
            )}

            {!isLoading && !hasSearched && stores.length === 0 && (
                <div className="flex flex-col items-center justify-center h-44 text-white/30">
                    <MapPin className="size-10 mb-3 opacity-20" />
                    <h3 className="text-sm font-medium text-white/50 mb-1">Share your location to find stores</h3>
                    <p className="text-[11px] text-center max-w-sm">
                        Click "Use My Location" or search by city/zip code to find local game stores near you.
                    </p>
                </div>
            )}

            {/* ========== STORE LIST ========== */}
            {displayStores.length > 0 && (
                <div className="space-y-2">
                    {displayStores.map(store => {
                        const isExpanded = expandedStore === store.id;
                        const isPremium = (store as any).isPremium;

                        return (
                            <div
                                key={store.id}
                                className={`border rounded-xl overflow-hidden transition-colors ${store.isWpnStore
                                    ? isPremium
                                        ? 'bg-amber-500/[0.04] border-amber-500/20 hover:border-amber-500/35'
                                        : 'bg-blue-500/[0.03] border-blue-500/15 hover:border-blue-500/30'
                                    : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                                    }`}
                            >
                                {/* Store card header */}
                                <button
                                    onClick={() => setExpandedStore(isExpanded ? null : store.id)}
                                    className="w-full px-4 py-3 flex items-center gap-3 text-left"
                                >
                                    {/* Store icon */}
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${store.isWpnStore
                                        ? isPremium
                                            ? 'bg-amber-500/20 border-amber-500/30'
                                            : 'bg-blue-500/20 border-blue-500/30'
                                        : 'bg-white/5 border-white/10'
                                        }`}>
                                        <Store className={`size-5 ${store.isWpnStore
                                            ? isPremium ? 'text-amber-400' : 'text-blue-400'
                                            : 'text-white/40'
                                            }`} />
                                    </div>

                                    {/* Store info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-white truncate">{store.name}</span>
                                            {store.isWpnStore && isPremium && (
                                                <span className="text-[8px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-full border border-amber-500/30 flex-shrink-0 flex items-center gap-0.5">
                                                    <Star className="size-2 fill-amber-400" />
                                                    WPN PREMIUM
                                                </span>
                                            )}
                                            {store.isWpnStore && !isPremium && (
                                                <span className="text-[8px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded-full border border-blue-500/30 flex-shrink-0">
                                                    WPN
                                                </span>
                                            )}
                                            {!store.isWpnStore && (
                                                <span className="text-[8px] px-1.5 py-0.5 bg-white/5 text-white/30 rounded-full border border-white/10 flex-shrink-0">
                                                    OSM
                                                </span>
                                            )}
                                            {store.hasEvents && (
                                                <span title="Has events"><Calendar className="size-3 text-amber-400/60 flex-shrink-0" /></span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-white/40 truncate">
                                            {[store.address, store.city, store.state].filter(Boolean).join(', ') || 'Address not listed'}
                                        </div>
                                    </div>

                                    {/* Distance */}
                                    {store.distance != null && (
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-sm font-medium text-amber-400 tabular-nums">
                                                {store.distance < 1
                                                    ? `${(store.distance * 5280).toFixed(0)} ft`
                                                    : `${store.distance.toFixed(1)} mi`}
                                            </div>
                                            <div className="text-[10px] text-white/30">away</div>
                                        </div>
                                    )}

                                    {isExpanded
                                        ? <ChevronUp className="size-4 text-white/30 flex-shrink-0" />
                                        : <ChevronDown className="size-4 text-white/30 flex-shrink-0" />}
                                </button>

                                {/* Expanded details */}
                                {isExpanded && (
                                    <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
                                        {/* Contact info */}
                                        <div className="grid grid-cols-2 gap-2">
                                            {store.phone && (
                                                <a
                                                    href={`tel:${store.phone}`}
                                                    className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                                                >
                                                    <Phone className="size-3.5 text-emerald-400" />
                                                    <span className="text-xs text-white/70">{store.phone}</span>
                                                </a>
                                            )}
                                            {store.website && (
                                                <a
                                                    href={store.website.startsWith('http') ? store.website : `https://${store.website}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                                                >
                                                    <Globe className="size-3.5 text-blue-400" />
                                                    <span className="text-xs text-white/70 truncate">Website</span>
                                                    <ExternalLink className="size-2.5 text-white/30 ml-auto" />
                                                </a>
                                            )}
                                        </div>

                                        {/* Opening hours if available (OSM data) */}
                                        {(store as any).openingHours && (
                                            <div className="flex items-start gap-2">
                                                <Calendar className="size-3.5 text-white/30 mt-0.5 flex-shrink-0" />
                                                <div className="text-xs text-white/50">
                                                    {(store as any).openingHours}
                                                </div>
                                            </div>
                                        )}

                                        {/* Full address */}
                                        <div className="flex items-start gap-2">
                                            <MapPin className="size-3.5 text-white/30 mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-white/50">
                                                {store.address && <div>{store.address}</div>}
                                                <div>
                                                    {[store.city, store.state, store.zipCode].filter(Boolean).join(', ')}
                                                    {store.country && store.country !== 'US' && store.country !== 'United States' && ` · ${store.country}`}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="grid grid-cols-2 gap-2">
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${store.name} ${store.address} ${store.city} ${store.state}`)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium transition-all border border-emerald-500/30"
                                            >
                                                <Navigation className="size-3.5" />
                                                Get Directions
                                                <ExternalLink className="size-3 opacity-60" />
                                            </a>
                                            {store.isWpnStore ? (
                                                <a
                                                    href={`https://locator.wizards.com/search?searchType=stores&query=${encodeURIComponent(store.name)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-xs font-medium transition-all border border-blue-500/30"
                                                >
                                                    <ShieldCheck className="size-3.5" />
                                                    View Events
                                                    <ExternalLink className="size-3 opacity-60" />
                                                </a>
                                            ) : (
                                                <a
                                                    href={`https://www.google.com/search?q=${encodeURIComponent(`${store.name} ${store.city} ${store.state} game store`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/60 rounded-lg text-xs font-medium transition-all border border-white/10"
                                                >
                                                    <Search className="size-3.5" />
                                                    Search Online
                                                    <ExternalLink className="size-3 opacity-60" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ========== DATA SOURCE FOOTER ========== */}
            <div className="flex items-start gap-2 px-1 text-[10px] text-white/25">
                <Info className="size-3 flex-shrink-0 mt-0.5" />
                <p>
                    WPN store data from Wizards of the Coast. Additional stores from OpenStreetMap contributors.
                    For events and full store details, visit the{' '}
                    <a href="https://locator.wizards.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400/50 hover:text-blue-400 underline">
                        Wizards Store & Event Locator
                    </a>.
                </p>
            </div>
        </div>
    );
}
